// ===============================
// src/pages/admin/RecivedOffersAdmin.jsx
// ===============================
import React, { Suspense, useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_DOMAIN } from "config";

import { Heading, Img, Button } from "../../components";
import AllOffersAdminLines from "../../components/AllOffersAdminLines";
import ApprovedOffersAdmin from "../../components/ApprovedOffersAdmin";
import ApproveOfferByProviderPopup from "components/ApproveOfferByProviderPopup";

const tabs = ["הצעות שאושרו", "הצעות חדשות"];
const STATUS_MAP = { "הצעות חדשות": 1, "הצעות שאושרו": 2 };

const sortOffersDesc = (offers) => {
  const arr = Array.isArray(offers) ? [...offers] : [];
  arr.sort((a, b) => {
    const da = new Date(a.offerDate || a.creation_date || 0).getTime();
    const db = new Date(b.offerDate || b.creation_date || 0).getTime();
    return db - da;
  });
  return arr;
};

export default function RecivedOffersAdmin({ currentUser }) {
  const [activeTab, setActiveTab] = useState("הצעות חדשות");

  const [showApprovedOfferPopuop, setShowApprovedOfferPopuop] = useState(false);
  const [selectedCar, setSelectedCar] = useState(null);
  const [selectedOffer, setSelectedOffer] = useState(null);

  const [offerGroups, setOfferGroups] = useState([]);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [meta, setMeta] = useState({ page: 1, pageSize: 10, totalItems: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => setPage(1), [activeTab]);

  const loadOffers = useCallback(async () => {
    setLoading(true);
    try {
      const status = STATUS_MAP[activeTab];

      const { data } = await axios.get(`${API_DOMAIN}/offer/getAdminReceivedOffers`, {
        params: { status, page, pageSize },
      });

      setMeta(data?.meta || { page: 1, pageSize, totalItems: 0, totalPages: 1 });

      const mapped = (data?.data || []).map((b) => ({
        car: {
          ...b.car,
          images: (b.images || []).map((u) => ({ image_url: u })),
        },
        offers: (b.offers || []).map((o) => ({
          id: o.id,

          buyerName: o.buyer_name || "",
          buyerContactName: o.buyer_contact_name,
          buyerPhoneNumber: o.buyer_phone_number,

          providerName: b?.car?.provider_name,
          providerBusinessName: b?.car?.provider_business_name,

          carPrice: b?.car?.asking_price,
          offerPrice: o.offered_price,
          price: o.offered_price,

          offerDate: o.creation_date,
          offerPosition: o.offer_position,
          collectionDate: o.collection_date,
          lastPaymentDate: o.last_payment_date,
          isReserved: o.is_reserved === 1,

          status: o.status,
          statusLabel: o.status === 1 ? "ממתין לאישור" : o.status === 2 ? "מאושר" : "נדחה",

          star: o.star,
          rating: o.rating,
          rate: o.rate,
        })),
      }));

      setOfferGroups(mapped);
    } catch (e) {
      console.error(e);
      setOfferGroups([]);
      setMeta((m) => ({ ...m, totalItems: 0, totalPages: 1 }));
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, pageSize]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (cancelled) return;
      await loadOffers();
    };

    run();
    const intervalId = setInterval(run, 120000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [currentUser?.id, loadOffers]);

  const handlePopupClose = () => setShowApprovedOfferPopuop(false);
  const UpdateShowApprovedOfferPopup = (state) => setShowApprovedOfferPopuop(!!state);

  const handleOfferApproved = (carId) => {
    setOfferGroups((prev) => prev.filter((g) => g.car.id !== carId));
  };

  const statusForNav = STATUS_MAP[activeTab];

  // ✅ הכפתור "כל ההצעות" עובר לתוך הקומפוננטות (שורה של רכב/שנה) דרך prop
  const preparedGroups = useMemo(() => {
    return (offerGroups || []).map((g) => {
      const all = sortOffersDesc(g.offers);

      const leftAction = (
        <div className="shrink-0 flex justify-start">
          <Button
            size="lg"
            shape="round"
            className="rounded-[16px] border border-solid border-gray-300 px-4 py-2 font-semibold bg-white"
            onClick={() => {
              navigate(`/admin/received-offers/${g.car.id}?status=${statusForNav}`, {
                state: { car: g.car, offers: g.offers, activeTab },
              });
            }}
          >
            כל ההצעות ({all.length})
          </Button>
        </div>
      );

      return {
        ...g,
        previewOffers: all.slice(0, 3),
        allOffersCount: all.length,
        leftAction,
      };
    });
  }, [offerGroups, navigate, statusForNav, activeTab]);

  return (
    <>
      <div className="flex bg-blue_gray-50 p-8 sm:p-4" style={{ width: "94%", paddingTop: "1rem" }}>
        <div className="w-[100%] items-start gap-4 md:flex-col" style={{ gap: "2%" }}>
          <div className="flex relative flex-1 flex-col gap-8 self-center rounded-[24px] bg-secondary-primary_white p-6 md:self-stretch sm:p-4">
            <header className="flex flex-col items-start gap-3">
              <div className="flex items-center justify-between gap-5 mt-5 self-stretch border-b border-solid border-gray-200 md:flex-col">
                <div className="flex items-center gap-3 w-full">
                  <Img src="/images/ad-123.svg" alt="ad" className="mr-auto" />

                  <div className="w-full flex justify-center gap-4">
                    {tabs.map((tab) => (
                      <div
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-4 py-2 cursor-pointer text-center ${
                          activeTab === tab ? "border-b-2 border-red-a700_03 text-black" : "text-gray-600"
                        }`}
                      >
                        {tab}
                      </div>
                    ))}
                  </div>

                  <Heading size="heading9xl" as="h4" className="!font-heebo text-[25px] font-bold lg:text-[21px]">
                    הצעות מחיר שהתקבלו
                  </Heading>

                  <Img src="/images/img_user_red_a700_03_1.svg" alt="user" className="h-[36px]" />
                </div>
              </div>
            </header>

            <div className="flex flex-col gap-6">
              <div>
                <Suspense fallback={<div>…טוען…</div>}>
                  {loading && <div className="text-center text-gray-500 py-4">טוען…</div>}

                  {preparedGroups.map((g) => (
                    <div key={g.car.id} className="mb-10">
                      {/* ✅ הטבלה (מקסימום 3) */}
                      {activeTab === "הצעות שאושרו" ? (
                        <ApprovedOffersAdmin
                          car={g.car}
                          rows={g.previewOffers}
                          onOfferApproved={handleOfferApproved}
                          updateShowApprovedOfferPopup={UpdateShowApprovedOfferPopup}
                          setSelectedCar={setSelectedCar}
                          setSelectedOffer={setSelectedOffer}
                          leftAction={g.leftAction} // ✅ הכפתור יוצג בשורת רכב/שנה בצד שמאל
                        />
                      ) : (
                        <AllOffersAdminLines
                          car={g.car}
                          rows={g.previewOffers}
                          onOfferApproved={handleOfferApproved}
                          updateShowApprovedOfferPopup={UpdateShowApprovedOfferPopup}
                          setSelectedCar={setSelectedCar}
                          setSelectedOffer={setSelectedOffer}
                          leftAction={g.leftAction} // ✅ הכפתור יוצג בשורת רכב/שנה בצד שמאל
                        />
                      )}
                    </div>
                  ))}
                </Suspense>

                {/* PAGINATION */}
                <div dir="rtl" className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-600">
                    עמוד {meta.page} מתוך {meta.totalPages} | סה״כ רכבים: {meta.totalItems}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      className="px-3 py-1 border rounded-md disabled:opacity-40"
                      disabled={meta.page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                    >
                      הקודם
                    </button>

                    <button
                      type="button"
                      className="px-3 py-1 border rounded-md disabled:opacity-40"
                      disabled={meta.page >= meta.totalPages}
                      onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
                    >
                      הבא
                    </button>

                    <div className="relative">
                      <select
                        dir="rtl"
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setPage(1);
                        }}
                        className="border rounded-md pl-9 pr-3 py-1 text-sm text-right bg-white appearance-none"
                        style={{
                          WebkitAppearance: "none",
                          MozAppearance: "none",
                          appearance: "none",
                          backgroundImage: "none",
                        }}
                      >
                        {[10, 5, 3, 2, 1].map((n) => (
                          <option key={n} value={n}>
                            {n} רכבים בעמוד
                          </option>
                        ))}
                      </select>

                      <Img
                        src="/images/img_vector_red_a700_03_10.svg"
                        alt="arrow"
                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-[10px] w-[10px] opacity-70"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {showApprovedOfferPopuop && (
              <ApproveOfferByProviderPopup onClose={handlePopupClose} car={selectedCar} offer={selectedOffer} />
            )}
          </div>
        </div>
      </div>
    </>
  );
}
